import { ChartsContainer } from '@anytag/shared-atoms';
import { HAS_NO_ESTIMATED_TEXT } from '@anytag/shared-constants';
import { SocialAccountType, useInfluencerProfileEstimatedAudienceQuery } from '@anytag/graphql';
import {
  AgeBreakdownChart,
  AgeBreakdownData,
  AreaBreakdownChart,
  AreaBreakdownData,
  GenderBreakdownChart,
  GenderBreakdownData
} from '@anytag/shared-molecules';
import { InfluencerSelectedAccountInfo } from '../../../types';
import { InfluencerProfileAudienceWrapper } from '../InfluencerProfileAudienceWrapper';

export interface InfluencerProfileEstimatedAudienceProps {
  socialAccount: InfluencerSelectedAccountInfo;
}

export const InfluencerProfileEstimatedAudience = ({ socialAccount }: InfluencerProfileEstimatedAudienceProps) => {
  const { data, loading } = useInfluencerProfileEstimatedAudienceQuery({
    variables: {
      socialAccountId: socialAccount.id,
      socialAccountType: socialAccount.type as SocialAccountType
    }
  });

  const estimatedData = data?.influencerProfileEstimatedAudience;
  const chartOpacityContent = { wrapperText: HAS_NO_ESTIMATED_TEXT, hasEstimatedDemographics: true };

  const formattedAudienceData = {
    genderData: {
      maleRate: estimatedData?.gender?.maleRate,
      femaleRate: estimatedData?.gender?.femaleRate
    } satisfies GenderBreakdownData,
    ageData: estimatedData?.ageGroup?.groups?.reduce<AgeBreakdownData>((acc, curr) => {
      if (curr) {
        acc.maleRate = [...(acc.maleRate || []), curr.maleRate];
        acc.femaleRate = [...(acc.femaleRate || []), curr.femaleRate];
        acc.ageGroup = [...(acc.ageGroup || []), { start: curr.group.at(0), end: curr.group.at(1) }];
      }

      return acc;
    }, {}),
    areaData: {
      followerRegionRate: estimatedData?.location?.regions?.map((region) => ({
        name: region.regionName,
        rate: region.rate
      })),
      followerCountryRate: estimatedData?.location?.countries?.reduce<Array<{ id: string; rate: number }>>(
        (acc, country) => (country ? [...acc, { id: country.countryId, rate: country.rate }] : acc),
        []
      )
    } satisfies AreaBreakdownData
  };

  return (
    <InfluencerProfileAudienceWrapper
      loading={loading}
      data={formattedAudienceData}
      hasEstimatedDemographics={chartOpacityContent.hasEstimatedDemographics}
    >
      {({ data: { genderData, ageData, areaData } }) => (
        <ChartsContainer
          css={{ height: '385px' }}
          elements={[
            <GenderBreakdownChart {...genderData} {...chartOpacityContent} />,
            <AgeBreakdownChart {...ageData} {...chartOpacityContent} />,
            <AreaBreakdownChart {...areaData} {...chartOpacityContent} hasCountriesRates />
          ]}
        />
      )}
    </InfluencerProfileAudienceWrapper>
  );
};
